<!-- src/components/QuestionModal.vue -->
<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center font-berthold-force"
        @keydown.esc.prevent="handleEsc"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 z-20" @click="close()"></div>

        <!-- Wrapper para respeitar o spacing do flowbite -->
        <div class="relative p-4 w-full max-w-md max-h-full z-20">
          <!-- Dialog -->
          <div
            ref="panelRef"
            class="relative rounded-lg shadow-sm bg-black focus:outline-none border border-[#d20a0a] max-h-[80vh] flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <!-- Header -->
            <div class="flex items-start justify-between gap-4 p-2 border-b border-gray-400">
              <div class="flex items-start gap-2">
                <!-- Avatar -->
                <img
                  src="@/assets/logo/icon.png"
                  alt="Grupo"
                  class="w-14 h-14 rounded-lg object-contain bg-black border border-[#333]"
                />

                <!-- Title -->
                <div 
                  class="mx-auto my-auto"
                >
                <!-- v-if="! loadSkeleton" -->
                  <h3
                    class="text-xl font-semibold text-white uppercase tracking-wider"
                  >
                    {{
                      data?.name.length > 67
                        ? data?.name.slice(0, 67).concat("...")
                        : data?.name
                    }}
                  </h3>
                </div>
              </div>

              <!-- Botão fechar -->
              <button
                type="button"
                class="my-auto text-gray-400 bg-transparent rounded-lg text-sm h-8 w-8 inline-flex justify-center items-center hover:text-[#d20a0a]"
                @click="close()"
              >
                <svg
                  class="w-5 h-5"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="flex-1 p-2 overflow-hidden">
              
              <!-- skeleton loader -->
              <div
                v-if="loadSkeleton"
                role="status"
                class="space-y-8 animate-pulse md:space-y-0 md:space-x-8 rtl:space-x-reverse md:flex md:items-center w-full"
              >
                <div
                  class="flex items-center justify-center w-full h-48 bg-gray-500 rounded-base sm:w-96"
                >
                  <svg
                    class="w-11 h-11 text-fg-disabled"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m3 16 5-7 6 6.5m6.5 2.5L16 13l-4.286 6M14 10h.01M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z"
                    />
                  </svg>
                </div>
                <div class="w-full">
                  <div class="h-2.5 bg-gray-500 rounded-full w-32 mb-4"></div>
                  <div class="h-2 bg-gray-500 rounded-full max-w-[480px] mb-2.5"></div>
                  <div class="h-2 bg-gray-500 rounded-full max-w-[260px] mb-2.5"></div>
                  <div class="h-2 bg-gray-500 rounded-full max-w-[440px] mb-2.5"></div>
                  <div class="h-2 bg-gray-500 rounded-full max-w-[210px] mb-2.5"></div>
                  <div class="h-2 bg-gray-500 rounded-full max-w-[360px]"></div>
                </div>
              </div>

              <!-- <template> -->
              <div
                class="grid grid-cols-1 md:grid-cols-2 gap-1 h-full text-white w-full"
                v-else
              >
                <!-- Coluna esquerda -->
                <div class="space-y-4 text-gray-300 text-start text-base md:text-sm">
                  <div>
                    <span class="block text-gray-400 uppercase">Grupo</span>
                    <p class="text-white font-medium">{{ data?.name }}</p>
                  </div>

                  <div>
                    <span class="block text-gray-400 uppercase">Descrição</span>
                    <p class="tracking-wider">{{ data?.description }}</p>
                  </div>

                  <div>
                    <span class="block text-gray-400 uppercase"
                      >treinamento</span
                    >
                    <p>{{ data?.event?.title ?? "—" }}</p>
                  </div>

                  <div class="flex gap-6">
                    <div>
                      <span class="block text-gray-400 uppercase">Módulos</span>
                      <p class="text-white">
                        {{ data?.event?.modules_count ?? "-" }}
                      </p>
                    </div>

                    <div>
                      <span class="block text-gray-400 uppercase">Aulas</span>
                      <p class="text-white">
                        {{ data?.event?.lessons_count ?? "-" }}
                      </p>
                    </div>

                    <!-- status da turma -->
                    <div>
                      <span class="block text-gray-400 uppercase">status</span>
                      <p 
                        class="font-medium py-1 rounded-full whitespace-nowrap tracking-wider lowercase"
                        style="background: none !important;"
                        :class="[
                          data?.is_active ? statusMap['passed'].classes : statusMap['failed'].classes,
                        ]"
                      >
                        {{ 
                          data?.is_active ? 'ativo' : 'desativado' ?? "-" 
                        }}
                      </p>
                    </div>

                  </div>
                </div>

                <!-- Coluna direita -->
                <div class="text-base md:text-sm flex flex-col h-full min-h-0 shrink-0">
                  <!-- line break -->
                  <span class="md:hidden my-2 border-t border-gray-400"></span>

                  <h4 class="uppercase tracking-wide text-gray-400 shrink-0">
                    Alunos na turma - {{ data?.customers.length }} <span class="lowercase tracking-wider" v-show="selectedStudents.length"> (exportar {{ selectedStudents.length }}) </span>
                  </h4>

                  <!-- Área scrollável (preparada) -->
                  <div class="h-auto max-h-[32vh] overflow-x-auto space-y-1.5">
                    <div
                      v-for="student in data.customers"
                      :key="student.id"
                      class="flex items-center justify-between gap-1 bg-gray-900/40 hover:bg-gray-900/60 transition rounded-lg px-2 py-1"
                    >
                      <!-- Lado esquerdo -->
                      <div class="flex items-center gap-1 min-w-0">
                        <!-- Checkbox -->
                        <input
                          type="checkbox"
                          :value="student.id"
                          v-model="selectedStudents"
                          class="rounded border-gray-700 bg-gray-800 focus:ring-0 accent-[#d20a0a]"
                        />

                        <!-- Nome -->
                        <span
                          class="text-gray-200 truncate tracking-wider uppercase"
                        >
                          {{
                            student?.name.length > 12 && !isMobile
                              ? student?.name.slice(0, 10).concat("...")
                              : student?.name
                          }}
                        </span>
                      </div>

                      <!-- Status -->
                      <span
                        class="font-medium px-2 py-1 rounded-full whitespace-nowrap tracking-wider lowercase"
                        :class="statusMap[student.status].classes"
                      >
                        {{ statusMap[student.status].label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- </template> -->
            </div>

            <!-- Rodapé -->
            <div
              class="flex items-center justify-between tracking-wider p-3"
              v-if="! loadSkeleton"
            >
              <!-- Botão fechar -->
              <button
                type="button"
                class="text-sm text-gray-400 hover:text-white transition"
                @click="close()"
              >
                Fechar
              </button>

              <!-- Botão exportar -->
              <button
                type="button"
                class="inline-flex items-center justify-center px-5 py-2.5 text-base uppercase text-white font-bold tracking-wide bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300"
                @click="exportGroup(data?.id, data?.name)"
              >
                Exportar
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { dataShared } from "@/shared/store";
import { initFlowbite } from "flowbite";
import { computed, onBeforeMount, ref } from "vue";

onBeforeMount(() => {
  initFlowbite();
});

const isMobile = computed(() => window.innerWidth < 768);

// modal
const open = computed(() => dataShared.getters.getModalGroupsOpen);
const data = computed(() => dataShared.getters.getDataGroupSelected); // dados do grupo selecionado
const loadSkeleton = computed(() => dataShared.getters.getSkeletonLoader);
const selectedStudents = ref([]);
const statusMap = {
  not_started: {
    label: "Não iniciado",
    classes: "bg-gray-900 text-gray-300",
  },
  in_progress: {
    label: "Em andamento",
    classes: "bg-blue-900 text-blue-300",
  },
  passed: {
    label: "Concluído",
    classes: "bg-green-900 text-green-300",
  },
  failed: {
    label: "Reprovado",
    classes: "bg-red-900 text-red-300",
  },
};

function exportGroup(id, name) {
  dataShared.dispatch('exportDataByGroupId', {id, name});
}

function close() {
  dataShared.dispatch("setModalOpen", false);
}

function handleEsc() {
  if (open) close();
}

</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

::-webkit-scrollbar {
  width: 2px;
  height: 1px;
}

::-webkit-scrollbar-thumb {
  background-color: #d20a0a;
  border-radius: 2px;
}

::-webkit-scrollbar-track {
  background-color: #000;
  border-radius: 2px;
}

</style>
